import FormWrapper from "../FormWrapper/FormWrapper";
import { DeliveryPlanPicker } from "../../../sharedComponents/DeliveryPlanCards";
import { resolveCurrencySymbol } from "../../../../utils/formatCurrency";

const isFruitItem = (item) =>
  Number(item?.productGroup) === 19 || parseInt(item?.productGroup, 10) === 19;

const numOr = (value, fallback) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
};

const itemWeight = (item, index, data) =>
  numOr(
    item?.itemWeight,
    index === 0 ? numOr(data.productWeight, 0) : 0,
  );

const itemDim = (item, key, fallback) => {
  const n = parseFloat(item?.[key]);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const DeliveryPlanStep = ({
  producDetails = [],
  productWeight,
  productLength,
  productWidth,
  productHeight,
  route,
  updateFields,
  selectedCurrency = "GBP",
  pricingDeliveryPlanId = "",
  fetchedFormData,
}) => {
  const items = Array.isArray(producDetails) && producDetails.length
    ? producDetails
    : [{}];
  const selectable = items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => !isFruitItem(item));
  const firstSelectedId = selectable.find(({ item }) => item.deliveryPlanId)
    ?.item?.deliveryPlanId;
  const currencySymbol = resolveCurrencySymbol(selectedCurrency);
  const routeName =
    fetchedFormData?.Route?.find((r) => String(r.id) === String(route))
      ?.routeName || "";

  const setItemPlan = (index, plan) => {
    const next = items.map((row, i) =>
      i === index
        ? {
            ...row,
            deliveryPlanId: plan?.deliveryPlanId || "",
            deliveryPlanName: plan?.name || "",
            deliveryPlanChoiceRequired: false,
          }
        : row,
    );
    updateFields({ producDetails: next });
  };

  return (
    <FormWrapper title="Delivery plan">
      <div className="w-full max-w-4xl">
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-orange-600">
          Shipping speed
        </p>
        <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mt-1">
          Choose a delivery plan
        </h2>
        <p className="text-stone-500 mt-2 mb-7 max-w-xl">
          Pick how quickly this shipment should arrive. The price is based on
          the weight and size you entered on the previous step.
        </p>

        {selectable.length === 0 ? (
          <div className="rounded-2xl border border-stone-200 bg-stone-50 px-5 py-8 text-stone-600">
            Fruit items do not need a delivery plan. Continue to the next step.
          </div>
        ) : (
          selectable.map(({ item, index }) => {
            const kg = itemWeight(item, index, { productWeight });
            const length = itemDim(item, "itemLength", numOr(productLength, 0));
            const width = itemDim(item, "itemWidth", numOr(productWidth, 0));
            const height = itemDim(item, "itemHeight", numOr(productHeight, 0));
            const volume = length * width * height || undefined;
            const label =
              item.itemDescription ||
              item.productTypeName ||
              item.productGroupName ||
              `Item ${index + 1}`;

            return (
              <section key={index} className="mb-8 last:mb-0">
                {selectable.length > 1 || label ? (
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="inline-flex items-center gap-2 rounded-full bg-stone-900 text-white text-xs font-semibold px-3 py-1">
                      {label}
                    </span>
                    <span className="text-sm text-stone-500">
                      {kg > 0 ? `${kg} kg` : "Weight needed"}
                      {length && width && height
                        ? ` · ${length} × ${width} × ${height}`
                        : ""}
                    </span>
                  </div>
                ) : null}
                <DeliveryPlanPicker
                  variant="hero"
                  routeId={route}
                  routeName={routeName}
                  kg={kg}
                  itemCategoryId={item.productGroup}
                  dimensionId={item.dimensionId}
                  volume={volume}
                  selectedId={item.deliveryPlanId}
                  preferredPlanId={
                    index === 0
                      ? pricingDeliveryPlanId || firstSelectedId
                      : firstSelectedId
                  }
                  currencySymbol={currencySymbol}
                  onSelect={(plan) => setItemPlan(index, plan)}
                  onOptionsChange={(rows) => {
                    const next = items.map((row, i) =>
                      i === index
                        ? {
                            ...row,
                            deliveryPlanChoiceRequired:
                              rows.length > 1 && !row.deliveryPlanId,
                          }
                        : row,
                    );
                    updateFields({ producDetails: next });
                  }}
                />
              </section>
            );
          })
        )}
      </div>
    </FormWrapper>
  );
};

export default DeliveryPlanStep;
